// Dynamic English sentence + question generator.
// Used by Test, SentenceBuilderGame, and LessonExamples to produce infinite
// fresh content from compact word banks. NO repeats.

const VOWELS = ["a", "e", "i", "o", "u"];

// ============================================================
// WORD BANKS
// ============================================================

// Subject pronouns + nouns. `s` flag indicates "third-person singular" so verbs need +s.
export const SUBJECTS = [
  // Pronouns
  { word: "I", thirdSing: false, kid: true },
  { word: "You", thirdSing: false, kid: true },
  { word: "He", thirdSing: true, kid: true },
  { word: "She", thirdSing: true, kid: true },
  { word: "It", thirdSing: true, kid: true },
  { word: "We", thirdSing: false, kid: true },
  { word: "They", thirdSing: false, kid: true },
  // Singular nouns (will need article when used in slots that require one)
  { word: "boy", thirdSing: true, isNoun: true, vowel: false, kid: true, emoji: "👦" },
  { word: "girl", thirdSing: true, isNoun: true, vowel: false, kid: true, emoji: "👧" },
  { word: "cat", thirdSing: true, isNoun: true, vowel: false, kid: true, emoji: "🐱" },
  { word: "dog", thirdSing: true, isNoun: true, vowel: false, kid: true, emoji: "🐶" },
  { word: "bird", thirdSing: true, isNoun: true, vowel: false, kid: true, emoji: "🐦" },
  { word: "elephant", thirdSing: true, isNoun: true, vowel: true, kid: true, emoji: "🐘" },
  { word: "owl", thirdSing: true, isNoun: true, vowel: true, kid: true, emoji: "🦉" },
  { word: "ant", thirdSing: true, isNoun: true, vowel: true, kid: true, emoji: "🐜" },
  { word: "octopus", thirdSing: true, isNoun: true, vowel: true, kid: true, emoji: "🐙" },
  { word: "rabbit", thirdSing: true, isNoun: true, vowel: false, kid: true, emoji: "🐰" },
  { word: "fish", thirdSing: true, isNoun: true, vowel: false, kid: true, emoji: "🐟" },
  { word: "horse", thirdSing: true, isNoun: true, vowel: false, kid: true, emoji: "🐴" },
  { word: "monkey", thirdSing: true, isNoun: true, vowel: false, kid: true, emoji: "🐒" },
  { word: "lion", thirdSing: true, isNoun: true, vowel: false, kid: true, emoji: "🦁" },
  { word: "teacher", thirdSing: true, isNoun: true, vowel: false, emoji: "👩‍🏫" },
  { word: "doctor", thirdSing: true, isNoun: true, vowel: false, emoji: "👨‍⚕️" },
  { word: "mother", thirdSing: true, isNoun: true, vowel: false, kid: true, emoji: "👩" },
  { word: "father", thirdSing: true, isNoun: true, vowel: false, kid: true, emoji: "👨" },
  { word: "baby", thirdSing: true, isNoun: true, vowel: false, kid: true, emoji: "👶" },
  // Plurals (no article needed)
  { word: "Birds", thirdSing: false, plural: true, kid: true, emoji: "🐦" },
  { word: "Cats", thirdSing: false, plural: true, kid: true, emoji: "🐱" },
  { word: "Dogs", thirdSing: false, plural: true, kid: true, emoji: "🐶" },
  { word: "Children", thirdSing: false, plural: true, kid: true, emoji: "👶" },
  { word: "People", thirdSing: false, plural: true, emoji: "🧑‍🤝‍🧑" },
  { word: "Bees", thirdSing: false, plural: true, kid: true, emoji: "🐝" },
];

// Verbs with both base form and third-person-singular (+s) form.
// Some are intransitive (no object), some transitive.
export const VERBS = [
  { base: "fly", third: "flies", trans: false, kid: true, emoji: "✈️" },
  { base: "run", third: "runs", trans: false, kid: true, emoji: "🏃" },
  { base: "swim", third: "swims", trans: false, kid: true, emoji: "🏊" },
  { base: "jump", third: "jumps", trans: false, kid: true, emoji: "🤸" },
  { base: "sleep", third: "sleeps", trans: false, kid: true, emoji: "😴" },
  { base: "sing", third: "sings", trans: false, kid: true, emoji: "🎤" },
  { base: "dance", third: "dances", trans: false, kid: true, emoji: "💃" },
  { base: "laugh", third: "laughs", trans: false, kid: true, emoji: "😂" },
  { base: "cry", third: "cries", trans: false, kid: true, emoji: "😢" },
  { base: "smile", third: "smiles", trans: false, kid: true, emoji: "😊" },
  { base: "walk", third: "walks", trans: false, kid: true, emoji: "🚶" },
  { base: "sit", third: "sits", trans: false, kid: true, emoji: "🪑" },
  { base: "play", third: "plays", trans: true, kid: true, emoji: "🎮" },
  { base: "eat", third: "eats", trans: true, kid: true, emoji: "🍽️" },
  { base: "drink", third: "drinks", trans: true, kid: true, emoji: "🥤" },
  { base: "read", third: "reads", trans: true, kid: true, emoji: "📖" },
  { base: "write", third: "writes", trans: true, emoji: "✍️" },
  { base: "watch", third: "watches", trans: true, kid: true, emoji: "📺" },
  { base: "like", third: "likes", trans: true, kid: true, emoji: "❤️" },
  { base: "love", third: "loves", trans: true, kid: true, emoji: "💕" },
  { base: "want", third: "wants", trans: true, emoji: "🙏" },
  { base: "have", third: "has", trans: true, emoji: "✊" },
  { base: "buy", third: "buys", trans: true, emoji: "🛒" },
  { base: "open", third: "opens", trans: true, emoji: "🚪" },
  { base: "see", third: "sees", trans: true, kid: true, emoji: "👀" },
  { base: "ride", third: "rides", trans: true, kid: true, emoji: "🚲" },
  { base: "throw", third: "throws", trans: true, kid: true, emoji: "🤾" },
  { base: "catch", third: "catches", trans: true, kid: true, emoji: "🤲" },
];

// Objects (nouns that can follow a transitive verb).
export const OBJECTS = [
  { word: "apple", vowel: true, kid: true, emoji: "🍎" },
  { word: "orange", vowel: true, kid: true, emoji: "🍊" },
  { word: "egg", vowel: true, kid: true, emoji: "🥚" },
  { word: "ice cream", vowel: true, kid: true, emoji: "🍦" },
  { word: "umbrella", vowel: true, kid: true, emoji: "☂️" },
  { word: "elephant", vowel: true, kid: true, emoji: "🐘" },
  { word: "owl", vowel: true, kid: true, emoji: "🦉" },
  { word: "ant", vowel: true, kid: true, emoji: "🐜" },
  { word: "banana", vowel: false, kid: true, emoji: "🍌" },
  { word: "book", vowel: false, kid: true, emoji: "📚" },
  { word: "pen", vowel: false, kid: true, emoji: "🖊️" },
  { word: "ball", vowel: false, kid: true, emoji: "⚽" },
  { word: "kite", vowel: false, kid: true, emoji: "🪁" },
  { word: "cake", vowel: false, kid: true, emoji: "🎂" },
  { word: "song", vowel: false, kid: true, emoji: "🎵" },
  { word: "letter", vowel: false, emoji: "✉️" },
  { word: "story", vowel: false, kid: true, emoji: "📖" },
  { word: "movie", vowel: false, kid: true, emoji: "🎬" },
  { word: "puzzle", vowel: false, kid: true, emoji: "🧩" },
  { word: "sandwich", vowel: false, kid: true, emoji: "🥪" },
  { word: "guitar", vowel: false, kid: true, emoji: "🎸" },
  { word: "bicycle", vowel: false, kid: true, emoji: "🚲" },
  { word: "dog", vowel: false, kid: true, emoji: "🐶" },
  { word: "fish", vowel: false, kid: true, emoji: "🐟" },
  { word: "milk", vowel: false, kid: true, emoji: "🥛" },
  { word: "water", vowel: false, kid: true, emoji: "💧" },
];

// ============================================================
// HELPERS
// ============================================================

function pick(arr) { return arr[Math.floor(Math.random() * arr.length)]; }
function pickN(arr, n) { return [...arr].sort(() => Math.random() - 0.5).slice(0, n); }
function startsWithVowelSound(word) {
  // Simple rule: starts with a/e/i/o/u → "an". (Ignores h/u edge cases — fine for beginners.)
  return VOWELS.includes(word.toLowerCase()[0]);
}
export function articleFor(word) { return startsWithVowelSound(word) ? "an" : "a"; }
function capFirst(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; }
function lower(s) { return (s || "").toLowerCase(); }

// ============================================================
// SENTENCE GENERATORS — by pattern
// ============================================================

export const PATTERNS = {
  S_only: "S_only",       // Just a subject ("Birds", "I")
  SV: "SV",               // Subject + Verb ("Birds fly")
  SVO: "SVO",             // Subject + Verb + Object ("Cats eat fish")
  ART_SV: "ART_SV",       // Article + Subject + Verb ("A bird flies")
  S_V_ART_O: "S_V_ART_O", // Subject + Verb + Article + Object ("I eat an apple")
  ART_S_V_ART_O: "ART_S_V_ART_O", // Full ("The boy eats an apple")
  THE_S_V: "THE_S_V",     // The + Subject + Verb ("The cat sleeps")
};

export function generateSentence(pattern, opts = {}) {
  const kidMode = opts.kid !== false; // default kid-friendly
  const filterKid = (arr) => arr.filter((w) => !kidMode || w.kid);

  const subjectPool = filterKid(SUBJECTS);
  const verbPool = filterKid(VERBS);
  const objectPool = filterKid(OBJECTS);

  switch (pattern) {
    case PATTERNS.S_only: {
      const s = pick(subjectPool);
      return {
        words: [{ text: s.word, role: "Subject" }],
        text: s.word,
        breakdown: { subject: s.word },
      };
    }

    case PATTERNS.SV: {
      const s = pick(subjectPool.filter((s) => s.plural || ["I", "You", "We", "They"].includes(s.word) || s.thirdSing));
      const v = pick(verbPool.filter((v) => !v.trans)); // intransitive only
      const verbForm = s.thirdSing && !s.plural ? v.third : v.base;
      const subjectText = s.isNoun ? capFirst(s.plural ? s.word : s.word) : s.word;
      return {
        words: [
          { text: subjectText, role: "Subject", emoji: s.emoji },
          { text: verbForm, role: "Verb", emoji: v.emoji },
        ],
        text: `${subjectText} ${verbForm}`,
        breakdown: { subject: subjectText, verb: verbForm },
      };
    }

    case PATTERNS.SVO: {
      // No articles — use plural subjects and plural-friendly objects
      const s = pick(subjectPool.filter((s) => s.plural || ["I", "You", "We", "They"].includes(s.word)));
      const v = pick(verbPool.filter((v) => v.trans));
      const o = pick(objectPool);
      const verbForm = s.thirdSing && !s.plural ? v.third : v.base;
      // Make objects mass-noun friendly by sometimes pluralising or just bare
      const objText = lower(o.word);
      return {
        words: [
          { text: s.word, role: "Subject", emoji: s.emoji },
          { text: verbForm, role: "Verb", emoji: v.emoji },
          { text: objText, role: "Object", emoji: o.emoji },
        ],
        text: `${s.word} ${verbForm} ${objText}`,
        breakdown: { subject: s.word, verb: verbForm, object: objText },
      };
    }

    case PATTERNS.ART_SV: {
      const s = pick(subjectPool.filter((s) => s.isNoun && !s.plural));
      const v = pick(verbPool.filter((v) => !v.trans));
      const article = articleFor(s.word);
      return {
        words: [
          { text: article, role: "Article", emoji: "📰" },
          { text: s.word, role: "Subject", emoji: s.emoji },
          { text: v.third, role: "Verb", emoji: v.emoji },
        ],
        text: `${capFirst(article)} ${s.word} ${v.third}`,
        breakdown: { article, subject: s.word, verb: v.third },
      };
    }

    case PATTERNS.S_V_ART_O: {
      const s = pick(subjectPool.filter((s) => ["I", "You", "He", "She", "We", "They"].includes(s.word)));
      const v = pick(verbPool.filter((v) => v.trans));
      const o = pick(objectPool);
      const verbForm = s.thirdSing ? v.third : v.base;
      const article = articleFor(o.word);
      return {
        words: [
          { text: s.word, role: "Subject", emoji: s.emoji },
          { text: verbForm, role: "Verb", emoji: v.emoji },
          { text: article, role: "Article", emoji: "📰" },
          { text: o.word, role: "Object", emoji: o.emoji },
        ],
        text: `${s.word} ${verbForm} ${article} ${o.word}`,
        breakdown: { subject: s.word, verb: verbForm, article, object: o.word },
      };
    }

    case PATTERNS.ART_S_V_ART_O: {
      const s = pick(subjectPool.filter((s) => s.isNoun && !s.plural));
      const v = pick(verbPool.filter((v) => v.trans));
      const o = pick(objectPool);
      const sArt = articleFor(s.word);
      const oArt = articleFor(o.word);
      return {
        words: [
          { text: sArt, role: "Article", emoji: "📰" },
          { text: s.word, role: "Subject", emoji: s.emoji },
          { text: v.third, role: "Verb", emoji: v.emoji },
          { text: oArt, role: "Article", emoji: "📰" },
          { text: o.word, role: "Object", emoji: o.emoji },
        ],
        text: `${capFirst(sArt)} ${s.word} ${v.third} ${oArt} ${o.word}`,
        breakdown: { article1: sArt, subject: s.word, verb: v.third, article2: oArt, object: o.word },
      };
    }

    case PATTERNS.THE_S_V: {
      const s = pick(subjectPool.filter((s) => s.isNoun && !s.plural));
      const v = pick(verbPool.filter((v) => !v.trans));
      return {
        words: [
          { text: "The", role: "Article", emoji: "📰" },
          { text: s.word, role: "Subject", emoji: s.emoji },
          { text: v.third, role: "Verb", emoji: v.emoji },
        ],
        text: `The ${s.word} ${v.third}`,
        breakdown: { article: "The", subject: s.word, verb: v.third },
      };
    }

    default:
      return generateSentence(PATTERNS.SVO, opts);
  }
}

// Generate N unique sentences for a given pattern.
export function generateSentences(pattern, count = 5, opts = {}) {
  const seen = new Set();
  const out = [];
  let safety = 0;
  while (out.length < count && safety++ < count * 8) {
    const s = generateSentence(pattern, opts);
    if (!seen.has(s.text)) {
      seen.add(s.text);
      out.push(s);
    }
  }
  return out;
}

// ============================================================
// DYNAMIC TEST QUESTIONS
// ============================================================

// Each lessonId maps to a question generator function. Each call returns ONE fresh MC.
const QUESTION_GENERATORS = {
  // Day 1: Subject identification
  subject: () => {
    const s = generateSentence(PATTERNS.SV);
    const otherWord = s.words[1].text;
    const opts = pickN([s.words[0].text, otherWord, "is", "an", "the"].filter((v, i, a) => a.indexOf(v) === i), 3);
    if (!opts.includes(s.words[0].text)) opts[0] = s.words[0].text;
    return {
      question: `Which word is the SUBJECT in: "${s.text}"?`,
      options: opts.sort(() => Math.random() - 0.5),
      answer: s.words[0].text,
      explanation: `${s.words[0].text} is doing the action — that's the subject.`,
    };
  },

  // Day 2: SV — pick the verb
  verb: () => {
    const s = generateSentence(PATTERNS.SV);
    const opts = pickN([s.words[1].text, s.words[0].text, "and", "to"], 3);
    if (!opts.includes(s.words[1].text)) opts[0] = s.words[1].text;
    return {
      question: `Which word is the VERB in: "${s.text}"?`,
      options: opts.sort(() => Math.random() - 0.5),
      answer: s.words[1].text,
      explanation: `${s.words[1].text} is the action — that's the verb.`,
    };
  },

  // Day 4: SVO — order the words
  svo: () => {
    const s = generateSentence(PATTERNS.SVO);
    const correct = s.text;
    const wrong1 = `${s.words[1].text} ${s.words[0].text} ${s.words[2].text}`;
    const wrong2 = `${s.words[2].text} ${s.words[1].text} ${s.words[0].text}`;
    return {
      question: `Which sentence is in correct Subject-Verb-Object order?`,
      options: [correct, wrong1, wrong2].sort(() => Math.random() - 0.5),
      answer: correct,
      explanation: `English follows Subject → Verb → Object: "${correct}"`,
    };
  },

  // Day 6 & 7: Articles a/an based on vowel rule
  articles: () => {
    const word = pick([...OBJECTS, ...SUBJECTS.filter((s) => s.isNoun)]);
    const correct = articleFor(word.word);
    return {
      question: `Pick the right article: "I have ___ ${word.word}."`,
      options: ["a", "an", "the"],
      answer: correct,
      explanation: `"${word.word}" starts with ${startsWithVowelSound(word.word) ? "a vowel sound" : "a consonant sound"} → use "${correct}".`,
    };
  },

  // Day 9: SVO + article on object
  article_object: () => {
    const s = generateSentence(PATTERNS.S_V_ART_O);
    const correctArt = s.breakdown.article;
    const wrongArt = correctArt === "a" ? "an" : "a";
    return {
      question: `Fill the blank: "${s.breakdown.subject} ${s.breakdown.verb} ___ ${s.breakdown.object}."`,
      options: [correctArt, wrongArt, "the"].sort(() => Math.random() - 0.5),
      answer: correctArt,
      explanation: `"${s.breakdown.object}" starts with ${startsWithVowelSound(s.breakdown.object) ? "a vowel sound" : "a consonant sound"} → "${correctArt}".`,
    };
  },

  // Day 11: The (definite article)
  the_article: () => {
    const fixtures = [
      { q: "___ sun rises in the east.", a: "The", e: "Only one sun → use 'The'." },
      { q: "Please close ___ door. (we both see it)", a: "The", e: "Both speakers know which door → 'The'." },
      { q: "I want ___ apple. (any apple)", a: "an", e: "Any one apple, vowel sound → 'an'." },
      { q: "She is ___ teacher.", a: "a", e: "One general teacher, consonant sound → 'a'." },
    ];
    const f = pick(fixtures);
    return {
      question: f.q,
      options: ["a", "an", "the"].sort(() => Math.random() - 0.5),
      answer: f.a.toLowerCase() === "the" ? "the" : f.a,
      explanation: f.e,
    };
  },

  // Mixed (final practice)
  mixed: () => {
    const types = ["subject", "verb", "svo", "articles", "article_object", "the_article"];
    return QUESTION_GENERATORS[pick(types)]();
  },
};

export function generateTestQuestion(generatorKey = "mixed") {
  const fn = QUESTION_GENERATORS[generatorKey] || QUESTION_GENERATORS.mixed;
  return fn();
}

export function generateTestQuestions(generatorKey = "mixed", count = 5) {
  const seen = new Set();
  const out = [];
  let safety = 0;
  while (out.length < count && safety++ < count * 8) {
    const q = generateTestQuestion(generatorKey);
    const key = q.question;
    if (!seen.has(key)) {
      seen.add(key);
      out.push(q);
    }
  }
  return out;
}

// Map lesson id → question generator key
export const LESSON_GENERATOR_MAP = {
  "subject-only": "subject",
  "sv-pattern": "verb",
  "sv-practice": "verb",
  "svo-pattern": "svo",
  "svo-practice": "svo",
  "vowel-rule": "articles",
  "article-sv": "articles",
  "article-sv-practice": "articles",
  "svo-article-object": "article_object",
  "svo-article-practice": "article_object",
  "the-article": "the_article",
  "articles-mixed": "mixed",
};
